import { Button, Group, SegmentedControl, Stack, Text, Title } from "@mantine/core";
import { IconChecklist, IconPlus } from "@tabler/icons-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { useCurrentUser } from "../../api/auth";
import { timeWindows, useTasks, type TaskListScope, type TaskPeriodKind } from "../../api/tasks";
import { NewTaskDrawer } from "./NewTaskDrawer";
import { TaskCard } from "./TaskCard";

/** Úkoly (F-TSK-03 až 05, 09): Denní / Týdenní / Měsíční, převzetí, přeskočení, splnění jedním klepnutím. */
export function TasksPage() {
  const { t } = useTranslation();
  const me = useCurrentUser();
  const canSeeAll = me.permissions.includes("TasksReview");
  const canCreate = me.permissions.includes("TasksCreate");
  const [period, setPeriod] = useState<TaskPeriodKind>("Day");
  const [scope, setScope] = useState<TaskListScope>("Mine");
  const [creating, setCreating] = useState(false);
  const tasks = useTasks(period, scope);

  const open = tasks.data?.filter((task) => task.status === "Open") ?? [];
  const done = tasks.data?.filter((task) => task.status !== "Open") ?? [];
  const byWindow = timeWindows
    .map((window) => [window, open.filter((task) => task.timeWindow === window)] as const)
    .filter(([, list]) => list.length > 0);

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={2}>{t("nav.tasks")}</Title>
        <Group gap="xs">
          {canSeeAll && (
            <Button component={Link} to="/ukoly/kontrola" variant="light" size="sm" leftSection={<IconChecklist size={18} />}>
              {t("tasks.review.title")}
            </Button>
          )}
          {canCreate && (
            <Button size="sm" leftSection={<IconPlus size={18} />} onClick={() => setCreating(true)}>
              {t("tasks.new")}
            </Button>
          )}
        </Group>
      </Group>

      <SegmentedControl fullWidth value={period} onChange={(v) => setPeriod(v as TaskPeriodKind)}
        data={(["Day", "Week", "Month"] as const).map((p) => ({ value: p, label: t(`tasks.period.${p}`) }))} />
      {canSeeAll && (
        <SegmentedControl size="xs" value={scope} onChange={(v) => setScope(v as TaskListScope)}
          data={[{ value: "Mine", label: t("tasks.scope.Mine") }, { value: "All", label: t("tasks.scope.All") }]} />
      )}

      {tasks.isSuccess && tasks.data.length === 0 && <Text c="dimmed">{t("tasks.empty")}</Text>}

      {byWindow.map(([window, list]) => (
        <Stack key={window} gap="xs">
          <Text fw={700} size="sm" c="dimmed" tt="uppercase">
            {t(`tasks.window.${window}`)}
          </Text>
          {list.map((task) => (
            <TaskCard key={task.id} task={task} />
          ))}
        </Stack>
      ))}

      {done.length > 0 && (
        <Stack gap="xs" mt="md">
          <Text fw={700} size="sm" c="dimmed" tt="uppercase">
            {t("tasks.doneSection", { count: done.length })}
          </Text>
          {done.map((task) => (
            <TaskCard key={task.id} task={task} />
          ))}
        </Stack>
      )}

      {creating && <NewTaskDrawer onClose={() => setCreating(false)} />}
    </Stack>
  );
}
